import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { CustomButton, DialogContainer, DialogTitle } from '../../../../common/components';

import { usePlans } from '../../../../hooks/usePlans';
import { useGroups } from '../../../../hooks/useGroups';
import { usePaymentMethods } from '../../../../hooks/usePaymentMethods';
import { useLegalEntities } from '../../../../hooks/useLegalEntities';
import { useConstructionTypes } from '../../../../hooks/useConstructionTypes';
import { usePreAdjudicationReasons } from '../../../../hooks/usePreAdjudicationReasons';
import { useReleaseReasons } from '../../../../hooks/useReleaseReasons';
import { usePermissions } from '../../../../hooks/usePermissions';

import { useContractIndividualEdit } from '../../hooks/useContractIndividualEdit';
import { DIALOG_TABS, TAB_LABELS } from '../../constants/contractsConstants';

import GeneralDataTab from './tabs/GeneralDataTab';
import SubscribersTab from './tabs/SubscribersTab';
import AdditionalsTab from './tabs/AdditionalsTab';
import CouponsTab from './tabs/CouponsTab';
import InvoicesTab from './tabs/InvoicesTab';
import ReleaseTab from './tabs/ReleaseTab';
import SubscriberDialog from './dialogs/SubscriberDialog';
import AdditionalDialog from './dialogs/AdditionalDialog';

/**
 * Diálogo de edición individual de contrato
 * Refactorizado completamente usando TanStack Query y componentes del common
 */
const ContractIndividualEditDialog = ({
  open,
  onClose,
  contract,
  onSave,
  isArchived = false,
  save,
  setSave,
}) => {
  const {
    localContract,
    activeTab,
    subscriberDialogOpen,
    selectedSubscriber,
    editModeSubscriber,
    matchingSubscribers,
    additionalDialogOpen,
    selectedAdditional,
    editModeAdditional,
    editDescription,
    snackbar,
    modal,
    additionals,
    handleInputChange,
    handleSelectChange,
    setActiveTab,
    handleSaveContract,
    handleOpenSubscriberDialog,
    handleCloseSubscriberDialog,
    handleEditSubscriber,
    handleSaveSubscriber,
    handleDeleteSubscriber,
    handleInputChangeSubscriber,
    handleSubscriberSelection,
    handleInputChangeDNISubscriber,
    handleOpenAdditionalDialog,
    handleCloseAdditionalDialog,
    handleEditAdditional,
    handleSaveAdditional,
    handleDeleteAdditional,
    handleInputChangeAdditional,
    handleReleaseContract,
    handleActivateMoraQuarantine,
    handleReactivateMoraQuarantine,
    isMoraActionLoading,
    handleCloseSnackbar,
    handleCloseModal,
  } = useContractIndividualEdit({
    contract,
    open,
    onSave,
    save,
    setSave,
    isArchived,
  });

  const { data: plans = [] } = usePlans();
  const { data: groups = [] } = useGroups();
  const { data: paymentMethods = [] } = usePaymentMethods();
  const { data: legalEntities = [] } = useLegalEntities();
  const { data: constructionTypes = [] } = useConstructionTypes();
  const { data: preAdjReasons = [] } = usePreAdjudicationReasons();
  const { data: releaseReasons = [] } = useReleaseReasons();
  const { permissions } = usePermissions();
  const canManageMora = Boolean(permissions?.canManageMora);
  const hasSubscribers = Boolean(localContract?.subscribers?.length);
  const isMoraQuarantineContract = Boolean(localContract?.is_mora_quarantine);
  const isArchivedContract = Boolean(isArchived || localContract?.is_archived);
  const isReadOnlyContract = Boolean(isArchived || isMoraQuarantineContract);
  const canActivateMora = hasSubscribers && !isArchivedContract;
  const canReactivateMora = isMoraQuarantineContract && !isArchivedContract;

  if (!open || !localContract) return null;

  const renderGeneralDataTab = () => (
    <GeneralDataTab
      localContract={localContract}
      isArchived={isArchived}
      isReadOnly={isReadOnlyContract}
      handleInputChange={handleInputChange}
      handleSelectChange={handleSelectChange}
      plans={plans}
      groups={groups}
      paymentMethods={paymentMethods}
      legalEntities={legalEntities}
      constructionTypes={constructionTypes}
      preAdjReasons={preAdjReasons}
    />
  );

  const renderSubscribersTab = () => (
    <SubscribersTab
      localContract={localContract}
      isArchived={isArchived}
      isReadOnly={isReadOnlyContract}
      handleOpenSubscriberDialog={handleOpenSubscriberDialog}
      handleEditSubscriber={handleEditSubscriber}
      handleDeleteSubscriber={handleDeleteSubscriber}
    />
  );

  const renderAdditionalsTab = () => (
    <AdditionalsTab
      localContract={localContract}
      isArchived={isArchived}
      isReadOnly={isReadOnlyContract}
      handleOpenAdditionalDialog={handleOpenAdditionalDialog}
      handleEditAdditional={handleEditAdditional}
      handleDeleteAdditional={handleDeleteAdditional}
    />
  );

  const renderCouponsTab = () => <CouponsTab localContract={localContract} />;

  const renderInvoicesTab = () => <InvoicesTab localContract={localContract} />;

  const renderReleaseTab = () => (
    <ReleaseTab
      localContract={localContract}
      handleInputChange={handleInputChange}
      handleSelectChange={handleSelectChange}
      handleReleaseContract={handleReleaseContract}
      releaseReasons={releaseReasons}
    />
  );

  return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <DialogContainer
        open={modal.open}
        onClose={handleCloseModal}
        size="small"
        minHeight="auto"
      >
        <DialogContent>
          <Typography>{modal.message}</Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={handleCloseModal} variant="base">
            Aceptar
          </CustomButton>
        </DialogActions>
      </DialogContainer>

      <DialogContainer open={open} onClose={onClose} size="fullWidth" minHeight="80vh">
        <DialogTitle>CONTRATO N° {localContract.contract_number || ''}</DialogTitle>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}
          >
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              scrollButtons="auto"
              variant="scrollable"
              sx={{
                '& .MuiTab-root': {
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                },
              }}
            >
              <Tab label={TAB_LABELS[DIALOG_TABS.GENERAL_DATA]} />
              <Tab label={TAB_LABELS[DIALOG_TABS.SUBSCRIBERS]} />
              <Tab label={TAB_LABELS[DIALOG_TABS.ADDITIONALS]} />
              <Tab label={TAB_LABELS[DIALOG_TABS.COUPONS]} />
              <Tab label={TAB_LABELS[DIALOG_TABS.INVOICES]} />
              {localContract.subscribers?.length > 0 &&
                !isArchived &&
                !isMoraQuarantineContract && (
                <Tab label={TAB_LABELS[DIALOG_TABS.RELEASE]} />
              )}
            </Tabs>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {activeTab === DIALOG_TABS.GENERAL_DATA && renderGeneralDataTab()}
            {activeTab === DIALOG_TABS.SUBSCRIBERS && renderSubscribersTab()}
            {activeTab === DIALOG_TABS.ADDITIONALS && renderAdditionalsTab()}
            {activeTab === DIALOG_TABS.COUPONS && renderCouponsTab()}
            {activeTab === DIALOG_TABS.INVOICES && renderInvoicesTab()}
            {activeTab === DIALOG_TABS.RELEASE && renderReleaseTab()}
          </Box>
        </Box>

        {isArchived ? (
          <DialogActions sx={{ flexShrink: 0, borderTop: '1px solid #e0e0e0', p: 2 }}>
            <CustomButton onClick={onClose} variant="base">
              Cerrar
            </CustomButton>
          </DialogActions>
        ) : isMoraQuarantineContract ? (
          <DialogActions sx={{ flexShrink: 0, p: 2, borderTop: '1px solid #e0e0e0' }}>
            <CustomButton
              onClick={handleReactivateMoraQuarantine}
              variant="danger"
              disabled={!canManageMora || isMoraActionLoading || !canReactivateMora}
            >
              Reactivar
            </CustomButton>
            <CustomButton onClick={onClose} variant="base">
              Cerrar
            </CustomButton>
          </DialogActions>
        ) : (
          <DialogActions sx={{ flexShrink: 0, p: 2, borderTop: '1px solid #e0e0e0' }}>
            <CustomButton
              onClick={
                localContract.is_mora_quarantine
                  ? handleReactivateMoraQuarantine
                  : handleActivateMoraQuarantine
              }
              variant="danger"
              disabled={
                !canManageMora ||
                isMoraActionLoading ||
                (localContract.is_mora_quarantine ? !canReactivateMora : !canActivateMora)
              }
              >
              {localContract.is_mora_quarantine ? 'Reactivar' : 'Pasar a cartera mora'}
            </CustomButton>
            {!localContract.is_mora_quarantine && !hasSubscribers && (
              <Typography variant="body2" color="error">
                No se puede pasar a mora: el contrato no tiene suscriptores asociados.
              </Typography>
            )}
            <CustomButton onClick={onClose} variant="warning">
              Cancelar
            </CustomButton>
            <CustomButton onClick={handleSaveContract} variant="base">
              Guardar
            </CustomButton>
          </DialogActions>
        )}
      </DialogContainer>

      <SubscriberDialog
        subscriberDialogOpen={subscriberDialogOpen}
        editModeSubscriber={editModeSubscriber}
        selectedSubscriber={selectedSubscriber}
        matchingSubscribers={matchingSubscribers}
        handleCloseSubscriberDialog={handleCloseSubscriberDialog}
        handleInputChangeSubscriber={handleInputChangeSubscriber}
        handleInputChangeDNISubscriber={handleInputChangeDNISubscriber}
        handleSubscriberSelection={handleSubscriberSelection}
        handleSaveSubscriber={handleSaveSubscriber}
      />

      <AdditionalDialog
        additionalDialogOpen={additionalDialogOpen}
        editModeAdditional={editModeAdditional}
        selectedAdditional={selectedAdditional}
        additionals={additionals}
        editDescription={editDescription}
        handleCloseAdditionalDialog={handleCloseAdditionalDialog}
        handleInputChangeAdditional={handleInputChangeAdditional}
        handleSaveAdditional={handleSaveAdditional}
      />
    </>
  );
};

export default ContractIndividualEditDialog;
